import { FC } from 'react';
import { Chip, makeStyles, useTheme } from '@material-ui/core';
import type { InstanceSource } from './types';

/**
 * Badge for the channel that produced a version: USER for human-authored
 * versions, AGENT/AI for system-authored proposals. "UNKNOWN" renders nothing.
 * Two palettes are defined so the badge remains legible in both light and dark
 * Backstage themes.
 */
const SOURCE_COLORS_LIGHT: Record<string, { bg: string; fg: string }> = {
  USER: { bg: '#deebff', fg: '#0747a6' },
  AGENT: { bg: '#eae6ff', fg: '#403294' },
  AI: { bg: '#e3fcef', fg: '#006644' },
};

const SOURCE_COLORS_DARK: Record<string, { bg: string; fg: string }> = {
  USER: { bg: '#0d2d6b', fg: '#4c9aff' },
  AGENT: { bg: '#2d2466', fg: '#c0b6f2' },
  AI: { bg: '#0a3622', fg: '#57d9a3' },
};

const useStyles = makeStyles({
  chip: {
    height: 18,
    fontSize: 10,
    fontWeight: 700,
    borderRadius: 3,
  },
});

interface SourceBadgeProps {
  source: InstanceSource;
}

export const SourceBadge: FC<SourceBadgeProps> = ({ source }) => {
  const classes = useStyles();
  const theme = useTheme();
  if (source === 'UNKNOWN') {
    return null;
  }
  const map =
    theme.palette.type === 'dark' ? SOURCE_COLORS_DARK : SOURCE_COLORS_LIGHT;
  const fallback =
    theme.palette.type === 'dark'
      ? { bg: '#3d4555', fg: '#b3bac5' }
      : { bg: '#dfe1e6', fg: '#5e6c84' };
  const palette = map[source] ?? fallback;
  return (
    <Chip
      label={source}
      size="small"
      className={classes.chip}
      style={{ backgroundColor: palette.bg, color: palette.fg }}
    />
  );
};

export default SourceBadge;
